import React, { useMemo } from 'react';

const ARRAY_SEP = '; ';

function normalizeToRows(results) {
  const rows = [];
  for (const r of results) {
    if (!r.success || !r.data) continue;
    const d = r.data;
    if (Array.isArray(d)) {
      d.forEach((item) => rows.push({ _url: r.url, ...item }));
    } else if (d.items && Array.isArray(d.items)) {
      d.items.forEach((item) => rows.push({ _url: r.url, ...item }));
    } else if (typeof d === 'object') {
      rows.push({ _url: r.url, ...d });
    }
  }
  return rows;
}

function getAllKeys(rows) {
  const set = new Set();
  rows.forEach((row) => Object.keys(row).forEach((k) => set.add(k)));
  return Array.from(set);
}

function valueToCell(value) {
  if (value == null) return '';
  if (Array.isArray(value)) return value.join(ARRAY_SEP);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function buildCsv(rows, columns) {
  const escape = (v) => {
    const s = String(v);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => escape(c)).join(',');
  const body = rows
    .map((row) => columns.map((col) => escape(valueToCell(row[col]))).join(','))
    .join('\n');
  const bom = '\uFEFF';
  return bom + header + '\n' + body;
}

function downloadBlob(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const Step3_Preview = ({ results, onBack, onComplete }) => {
  const rows = useMemo(() => normalizeToRows(results), [results]);
  const columns = useMemo(() => getAllKeys(rows), [rows]);
  const successCount = results.filter((r) => r.success).length;

  const handleExportCsv = () => {
    const csv = buildCsv(rows, columns);
    downloadBlob(csv, 'flexible-extract.csv', 'text/csv;charset=utf-8');
  };

  const handleExportJson = () => {
    const json = JSON.stringify(rows, null, 2);
    downloadBlob(json, 'flexible-extract.json', 'application/json');
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 4: Preview & export</h2>
        <p className="text-gray-600">
          {rows.length} row{rows.length !== 1 ? 's' : ''} from {successCount} URL{successCount !== 1 ? 's' : ''}. Export as CSV or JSON.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleExportCsv}
          disabled={rows.length === 0}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 font-medium"
        >
          Export CSV
        </button>
        <button
          type="button"
          onClick={handleExportJson}
          disabled={rows.length === 0}
          className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 font-medium"
        >
          Export JSON
        </button>
      </div>

      {rows.length > 0 ? (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                {columns.map((col) => (
                  <th key={col} className="px-3 py-2 text-left font-medium text-gray-700 border-b border-gray-200">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 50).map((row, i) => (
                <tr key={i} className="border-b border-gray-100">
                  {columns.map((col) => (
                    <td key={col} className="px-3 py-2 text-gray-900 max-w-xs truncate" title={valueToCell(row[col])}>
                      {valueToCell(row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 50 && (
            <p className="px-3 py-2 text-xs text-gray-500 bg-gray-50">Showing first 50 of {rows.length} rows. Export for full data.</p>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-600">No rows to show.</div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onComplete}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default Step3_Preview;
